import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StepListLookupModal from './StepListLookupModal';
import API_BASE_URL from '../../../../../Constants';

// unchanged arrays
const jobTypeOptions = ['Inspection', 'Maintenance', 'Modernization'];
const stepsLevelOptions = ['Level 1', 'Level 2', 'Level 3'];
const dispatchClassOptions = ['Normal', 'Urgent', 'Overnight'];
const dispatchSubClassOptions = ['Sub-A', 'Sub-B', 'Sub-C'];

export default function StepListSetupModal({
  open,
  onClose,
  currentStepListId = null,
  currentAgencyId = '',
  currentTestType = '',
}) {
  const [stepListId, setStepListId] = useState(currentStepListId);
  const [stepListDescription, setStepListDescription] = useState('');
  const [agencyId, setAgencyId] = useState(currentAgencyId);
  const [testType, setTestType] = useState(currentTestType);
  const [typeOfJob, setTypeOfJob] = useState('');
  const [stepsLevel, setStepsLevel] = useState('');
  const [dispatchClass, setDispatchClass] = useState('');
  const [dispatchSubClass, setDispatchSubClass] = useState('');

  // CHANGED: Store each item as { stepListItemId, stepNumber, stepName }
  const [checklistItems, setChecklistItems] = useState([]);

  const [openLookup, setOpenLookup] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!stepListId) {
      setStepListDescription('');
      setChecklistItems([]);
      return;
    }

    async function fetchStepList() {
      try {
        const r = await fetch(`${API_BASE_URL}/api/step-lists/${stepListId}`, {
          credentials: 'include',
        });
        if (!r.ok) throw new Error(`Failed to fetch. Status: ${r.status}`);
        const d = await r.json();
        setAgencyId(String(d.agencyId ?? ''));
        setTestType(d.testId || '');
        setStepListDescription(d.stepListDescription || '');
        if (d.type) setTypeOfJob(d.type);
        if (d.dispatchClass) setDispatchClass(d.dispatchClass);
        if (d.dispatchSubClass) setDispatchSubClass(d.dispatchSubClass);

        // CHANGED: store each step item with stepListItemId
        if (Array.isArray(d.items)) {
          const sorted = d.items.sort((a, b) => a.stepNumber - b.stepNumber);
          setChecklistItems(sorted.map(it => ({
            stepListItemId: it.stepListItemId || null,
            stepNumber: it.stepNumber,
            stepName: it.stepName || '',
          })));
        } else {
          setChecklistItems([]);
        }
      } catch {}
    }
    fetchStepList();
  }, [open, stepListId]);

  function handleCancel() {
    onClose(null);
  }

  // Keep your entire-list save method if you want it, but single-item changes happen below
  async function handleSave() {
    const p = {
      stepListId: stepListId || null,
      agencyId: parseInt(agencyId || '0', 10),
      testId: testType,
      stepListDescription,
      type: typeOfJob,
      dispatchClass,
      dispatchSubClass,
      items: checklistItems.map((item, i) => ({
        stepListItemId: item.stepListItemId || null,
        stepNumber: i + 1,
        stepName: item.stepName || '',
      })),
    };
    try {
      let url = `${API_BASE_URL}/api/step-lists`;
      let m = 'POST';
      if (stepListId) {
        url = `${API_BASE_URL}/api/step-lists/${stepListId}`;
        m = 'PUT';
      }
      const r = await fetch(url, {
        method: m,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(p),
      });
      if (!r.ok) throw new Error(`Save failed: ${r.status}`);
      const j = await r.json();
      onClose(j);
    } catch {
      alert('Failed to save step list.');
    }
  }

  function handleClear() {
    setStepListId(null);
    setStepListDescription('');
    setAgencyId('');
    setTestType('');
    setTypeOfJob('');
    setDispatchClass('');
    setDispatchSubClass('');
    setChecklistItems([]);
  }

  // CHANGED: handleChange => local update + single-item call
  async function handleChecklistChange(idx, newName) {
    setChecklistItems(prev => {
      const newItems = [...prev];
      const item = { ...newItems[idx] };
      item.stepName = newName;
      newItems[idx] = item;
      return newItems;
    });
  }

  // CHANGED: On blur => if stepListItemId is null => POST; else PUT
  // This ensures a single row update each time user finishes editing a row
  async function handleBlurRow(idx) {
    const row = checklistItems[idx];
    if (!row.stepName.trim()) {
      // empty step => skip
      return;
    }
    try {
      if (!stepListId) {
        alert('No stepListId set. Please create or select a StepList first.');
        return;
      }

      // if no stepListItemId => create
      if (!row.stepListItemId) {
        // POST /api/step-list-items
        const payload = {
          stepListId: parseInt(stepListId, 10),
          stepName: row.stepName,
          stepNumber: row.stepNumber, // or idx+1
        };
        const resp = await fetch(`${API_BASE_URL}/api/step-list-items`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!resp.ok) throw new Error(`POST item failed => ${resp.status}`);
        const created = await resp.json();
        // update local state with new stepListItemId
        setChecklistItems(prev => {
          const newItems = [...prev];
          newItems[idx] = {
            ...newItems[idx],
            stepListItemId: created.stepListItemId,
            stepNumber: created.stepNumber,
            stepName: created.stepName,
          };
          return newItems;
        });
      } else {
        // existing item => do PUT /api/step-list-items/{id}
        const payload = {
          stepListItemId: row.stepListItemId,
          stepListId: parseInt(stepListId, 10),
          stepName: row.stepName,
          stepNumber: row.stepNumber,
        };
        const resp = await fetch(`${API_BASE_URL}/api/step-list-items/${row.stepListItemId}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!resp.ok) throw new Error(`PUT item failed => ${resp.status}`);
        const updated = await resp.json();
        // update local state if needed
        setChecklistItems(prev => {
          const newItems = [...prev];
          newItems[idx] = {
            stepListItemId: updated.stepListItemId,
            stepNumber: updated.stepNumber,
            stepName: updated.stepName,
          };
          return newItems;
        });
      }
    } catch (err) {
      console.error(err);
      alert('Failed to save step item');
    }
  }

  function handleOpenLookup() {
    setOpenLookup(true);
  }
  function handleCloseLookup(id, desc) {
    setOpenLookup(false);
    if (id) {
      setStepListId(id);
      setStepListDescription(desc || '');
    }
  }

  // CHANGED: handleAddRow => create a blank row locally
  // We can also call POST immediately if you prefer
  function handleAddRow() {
    const newItem = {
      stepListItemId: null,
      stepNumber: checklistItems.length + 1,
      stepName: '',
    };
    setChecklistItems(prev => [...prev, newItem]);
  }

  // CHANGED: handleRemove => if row has stepListItemId => do DELETE
  async function handleRemoveRow(idx) {
    const row = checklistItems[idx];
    if (row.stepListItemId) {
      try {
        const url = `${API_BASE_URL}/api/step-list-items/${row.stepListItemId}`;
        const resp = await fetch(url, {
          method: 'DELETE',
          credentials: 'include',
        });
        if (!resp.ok) throw new Error(`DELETE item failed => ${resp.status}`);
      } catch (err) {
        console.error(err);
        alert('Failed to delete step item');
        return;
      }
    }
    // remove from local state
    setChecklistItems(prev => prev.filter((_, i) => i !== idx));
  }

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      hideBackdrop
      disableEnforceFocus
      disableAutoFocus
      PaperProps={{
        style: {
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '700px',
          height: '500px',
          zIndex: 3000,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      style={{ zIndex: 3000 }}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-300">
        <h2 className="text-xl font-bold">Step List Setup</h2>
        <button onClick={handleCancel} className="p-1 text-gray-500 hover:text-gray-700">
          X
        </button>
      </div>

      <DialogContent
        dividers
        style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ flex: '0 0 auto' }}>
          <label className="font-semibold mb-1 block">Step List ID:</label>
          <div className="flex items-center space-x-2 mb-3">
            <input
              type="text"
              className="border border-gray-300 rounded p-1 w-24"
              value={stepListId || ''}
              onChange={(e) => setStepListId(e.target.value)}
            />
            <button onClick={handleOpenLookup} className="p-1 rounded bg-gray-200 hover:bg-gray-300">
              <SearchIcon />
            </button>
          </div>
          {stepListDescription && (
            <div className="mb-3">
              <label className="font-semibold block">Description:</label>
              <p className="text-gray-700">{stepListDescription}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-semibold block">Agency ID:</label>
              <input
                type="text"
                className="border border-gray-300 rounded p-1 w-full"
                value={agencyId}
                onChange={(e) => setAgencyId(e.target.value)}
              />
            </div>
            <div>
              <label className="font-semibold block">Test Type:</label>
              <input
                type="text"
                className="border border-gray-300 rounded p-1 w-full"
                value={testType}
                onChange={(e) => setTestType(e.target.value)}
              />
            </div>
            <div>
              <label className="font-semibold block">Type of Job:</label>
              <select
                className="border border-gray-300 rounded p-1 w-full"
                value={typeOfJob}
                onChange={(e) => setTypeOfJob(e.target.value)}
              >
                <option value="">--</option>
                {jobTypeOptions.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-semibold block">Steps Level:</label>
              <select
                className="border border-gray-300 rounded p-1 w-full"
                value={stepsLevel}
                onChange={(e) => setStepsLevel(e.target.value)}
              >
                <option value="">--</option>
                {stepsLevelOptions.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-semibold block">Dispatch Class:</label>
              <select
                className="border border-gray-300 rounded p-1 w-full"
                value={dispatchClass}
                onChange={(e) => setDispatchClass(e.target.value)}
              >
                <option value="">--</option>
                {dispatchClassOptions.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-semibold block">Dispatch Subclass:</label>
              <select
                className="border border-gray-300 rounded p-1 w-full"
                value={dispatchSubClass}
                onChange={(e) => setDispatchSubClass(e.target.value)}
              >
                <option value="">--</option>
                {dispatchSubClassOptions.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div style={{ flex: '1 1 auto', overflow: 'auto', marginTop: '10px' }}>
          <label className="font-semibold mb-2 block">Checklist Items (dynamic):</label>
          <table className="w-full border border-gray-300 text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 border text-center w-12">#</th>
                <th className="p-2 border">Instruction</th>
                <th className="p-2 border text-center w-16">Remove</th>
              </tr>
            </thead>
            <tbody>
              {checklistItems.map((row, idx) => (
                <tr key={idx} className="border-b border-gray-300">
                  <td className="p-2 border text-center">{idx + 1}</td>
                  <td className="p-2 border">
                    <input
                      type="text"
                      className="border border-gray-300 rounded p-1 w-full"
                      value={row.stepName}
                      onChange={(e) => handleChecklistChange(idx, e.target.value)}
                      onBlur={() => handleBlurRow(idx)} // CHANGED: single-item call
                    />
                  </td>
                  <td className="p-2 border text-center">
                    <button
                      onClick={() => handleRemoveRow(idx)}
                      className="text-red-600 hover:text-red-800"
                      style={{ cursor: 'pointer' }}
                    >
                      X
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Button to add a new row */}
          <button
            onClick={handleAddRow}
            className="mt-2 px-3 py-1 bg-green-200 hover:bg-green-300 rounded"
          >
            + Add Step
          </button>
        </div>
      </DialogContent>

      <div className="flex justify-end space-x-3 p-4 border-t border-gray-300">
        <button
          onClick={handleCancel}
          className="px-4 py-1 bg-gray-300 text-black rounded hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          onClick={handleClear}
          className="px-4 py-1 bg-yellow-300 hover:bg-yellow-400 rounded"
        >
          Clear
        </button>
        <button
          onClick={handleSave} // Entire-list approach if you want
          className="px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Save
        </button>
      </div>

      {openLookup && (
        <StepListLookupModal open={openLookup} onClose={handleCloseLookup} />
      )}
    </Dialog>
  );
}
